import { Controller } from 'stimulus'
import DataTable from 'datatables.net-bs5'
import 'datatables.net-bs5/css/dataTables.bootstrap5.css'

const ENGLISH_COLUMN_INDEX = 2
const DEFAULT_ENGLISH_SORT = 'asc'

function cellText(cell) {
  if (!cell) {
    return ''
  }

  const textarea = cell.querySelector('textarea')
  if (textarea) {
    return textarea.value
  }

  const code = cell.querySelector('code')
  if (code) {
    return code.textContent.trim()
  }

  return cell.textContent.trim()
}

function rowSearchText(row) {
  return Array.from(row.cells)
    .map((cell) => cellText(cell))
    .join(' ')
    .toLowerCase()
}

if (!DataTable.ext.order['dom-textarea']) {
  DataTable.ext.order['dom-textarea'] = function (settings, col) {
    return settings.aoData.map((row) => {
      const cell = row.anCells ? row.anCells[col] : null
      return cellText(cell).toLowerCase()
    })
  }
}

export default class extends Controller {
  static targets = ['table', 'search', 'reset']

  connect() {
    this.searchQuery = this.hasSearchTarget ? this.searchTarget.value : ''

    if (!this.hasTableTarget) {
      return
    }

    if (DataTable.isDataTable(this.tableTarget)) {
      this.dataTable = new DataTable.Api(this.tableTarget)
      this.applySearch()
      return
    }

    const controller = this

    this.dataTable = new DataTable(this.tableTarget, {
      paging: false,
      info: false,
      searching: false,
      order: [[ENGLISH_COLUMN_INDEX, DEFAULT_ENGLISH_SORT]],
      orderMulti: false,
      autoWidth: false,
      layout: {
        topStart: null,
        topEnd: null,
        bottomStart: null,
        bottomEnd: null,
      },
      columns: [
        { orderable: false },
        { orderable: false },
        { orderable: true, orderDataType: 'dom-textarea', orderSequence: ['asc', 'desc'] },
        { orderable: false },
      ],
      drawCallback() {
        controller.syncEnglishSortIcon()
        controller.applySearch()
      },
    })
  }

  disconnect() {
    if (this.dataTable) {
      this.dataTable.destroy()
      this.dataTable = null
    }
  }

  handleSearch(event) {
    this.searchQuery = event.target.value
    this.applySearch()
    this.toggleResetButton()
  }

  resetSearch() {
    if (!this.hasSearchTarget) {
      return
    }

    this.searchQuery = ''
    this.searchTarget.value = ''
    this.applySearch()
    this.toggleResetButton()
  }

  applySearch() {
    if (!this.hasTableTarget) {
      return
    }

    const query = this.searchQuery.trim().toLowerCase()

    this.tableTarget.querySelectorAll('tbody tr').forEach((row) => {
      const match = query === '' || rowSearchText(row).includes(query)
      row.classList.toggle('d-none', !match)
    })
  }

  toggleResetButton() {
    if (!this.hasResetTarget) {
      return
    }

    this.resetTarget.classList.toggle('d-none', this.searchQuery.trim() === '')
  }

  syncEnglishSortIcon() {
    const englishHeader = this.tableTarget.querySelector(
      'thead th.dynamic-translations-table__english-column',
    )
    if (!englishHeader) {
      return
    }

    const iconName = this.englishSortDirection() === 'asc' ? 'chevron-down' : 'chevron-up'
    const title = englishHeader.querySelector('.dt-column-title') || englishHeader
    let iconWrap = englishHeader.querySelector('.dynamic-translations-sort-icon')

    if (!iconWrap) {
      iconWrap = document.createElement('span')
      iconWrap.className = 'dynamic-translations-sort-icon btn p-0 d-inline-flex align-items-center'
      title.insertAdjacentElement('afterend', iconWrap)
    }

    iconWrap.innerHTML = this.featherIconMarkup(iconName)
  }

  englishSortDirection() {
    if (!this.dataTable) {
      return DEFAULT_ENGLISH_SORT
    }

    const order = this.dataTable.order()
    if (!Array.isArray(order) || order.length === 0) {
      return DEFAULT_ENGLISH_SORT
    }

    const entry = order[0]
    if (Array.isArray(entry)) {
      return entry[1]
    }

    return entry.dir || DEFAULT_ENGLISH_SORT
  }

  featherIconMarkup(iconName) {
    if (typeof feather !== 'undefined' && feather.icons[iconName]) {
      return feather.icons[iconName].toSvg({ class: 'align-middle' })
    }

    return `<i class="align-middle" data-feather="${iconName}"></i>`
  }
}
